import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../components/AppToast";
import { fetchStockTransferInList } from "../../services/stockTransferIn/stockTransferIn";

const baseColumns = [
  { field: "transferInNo", headerName: "Transfer In No.", width: 150 },
  { field: "referenceSto", headerName: "Ref. STO", width: 130 },
  { field: "sourceLocation", headerName: "Ship-from", width: 150 },
  { field: "receivingLocation", headerName: "Receiving", width: 150 },
  { field: "status", headerName: "Status", width: 100 },
  { field: "totalQty", headerName: "Total Qty", width: 100 },
  { field: "totalAmount", headerName: "Total Amount", width: 120 },
  { field: "receivedDate", headerName: "Received", width: 120 },
  { field: "createdAt", headerName: "Created", width: 110 },
];

export default function StockTransferInList({ onCreateClick, onView, refreshKey = 0 }) {
  const selectedFacility = useSelector((state) => state?.facility?.selectedFacility);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [listLoading, setListLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const menuRowRef = useRef(null);

  const loadData = useCallback(async () => {
    setListLoading(true);
    try {
      const { rows: apiRows, total: apiTotal } = await fetchStockTransferInList({
        page: page + 1,
        limit: pageSize,
        status: "all",
        destinationLocationId: selectedFacility?.id || undefined,
      });
      setRows(apiRows);
      setTotal(typeof apiTotal === "number" ? apiTotal : apiRows.length);
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error(err?.message || "Failed to load stock transfer in list");
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize, selectedFacility?.id]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const handleDataRefresh = async () => {
    await loadData();
  };

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            menuRowRef.current = params.row;
            setMenuRow(params.row);
            setMenuAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  return (
    <>
      {listLoading && (
        <p className="text-muted small mb-2 mb-md-0">Loading stock transfer in records…</p>
      )}
      <CommonDataTable
        id="stock-transfer-in-list"
        allPossibleColumns={[actionColumn, ...baseColumns]}
        initialVisible={["__actions", ...baseColumns.map((c) => c.field)]}
        initialRows={rows}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        height={500}
        showCreateButton
        onCreateClick={onCreateClick}
        checkboxSelection
        enableImportButton={false}
        enableExportButton={false}
        forInventory
        onDataRefresh={handleDataRefresh}
        readOnly
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => {
          setMenuAnchorEl(null);
          menuRowRef.current = null;
        }}
      >
        <MenuItem
          onClick={() => {
            const row = menuRowRef.current ?? menuRow;
            setMenuAnchorEl(null);
            menuRowRef.current = null;
            if (row) onView?.(row);
          }}
        >
          <i className="bi bi-eye me-2" />
          View
        </MenuItem>
      </Menu>
    </>
  );
}
